import { useNavigate } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bank,
  Plus,
  CircleNotch,
  PencilSimple,
  Pause,
  Play,
  Star,
  Wallet,
  Vault,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  fetchBankAccounts,
  setBankAccountActive,
  formatSortCode,
  formatAccountMasked,
  type BankAccount,
  type BankAccountType,
} from '@/lib/bankAccountsApi';
import { BankAccountModal } from '@/components/bank-accounts/BankAccountModal';
import { getApiErrorMessage } from '@/lib/api';
import { useAuthStore } from '@/features/auth/store';
import { canMutateFinancials } from '@/lib/pupilUiBoundary';
import { BackButton } from '@/components/shared/BackButton';

function typeBadge(t: BankAccountType): { label: string; classes: string; icon: React.ReactNode } {
  switch (t) {
    case 'operating':
      return {
        label: 'Operating',
        classes: 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30',
        icon: <Wallet size={11} weight="duotone" />,
      };
    case 'trust':
      return {
        label: 'Trust',
        classes: 'bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-500/30',
        icon: <Vault size={11} weight="duotone" />,
      };
    case 'other':
      return {
        label: 'Other',
        classes: 'bg-zinc-100 text-zinc-700 dark:text-zinc-300 border-zinc-200',
        icon: <Bank size={11} weight="duotone" />,
      };
  }
}

export default function BankAccountsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const isAdmin = currentUser?.memberType === 'admin';
  const myBarristerId = currentUser?.barristerId ?? null;

  // Per docs/14: barristers default to "My accounts" (their own personal
  // accounts); chambers admins default to "All" (their chambers + every
  // barrister's roll-up); clerks see chambers-shared only and have no
  // toggle.
  type Scope = 'mine' | 'chambers' | 'all';
  const [scope, setScope] = useState<Scope>(myBarristerId ? 'mine' : 'all');

  const [typeFilter, setTypeFilter] = useState<BankAccountType | undefined>();
  const [activeFilter, setActiveFilter] = useState<'true' | 'false' | undefined>('true');
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<BankAccount | null>(null);

  const accountsQ = useQuery({
    queryKey: ['bank-accounts', { scope, type: typeFilter, isActive: activeFilter }],
    queryFn: () =>
      fetchBankAccounts({
        scope: scope === 'all' ? undefined : scope,
        type: typeFilter,
        isActive: activeFilter,
      }),
  });

  const canEditAccount = (a: BankAccount): boolean => {
    if (isAdmin) return true;
    if (a.ownerType === 'barrister' && a.barristerOwnerId === myBarristerId) return true;
    return false;
  };

  // A pupil HAS a barrister profile, so this was true for them and drew "Add account" —
  // POST /bank-accounts answers 403 (bank-accounts service excludes pupils from writes).
  const canCreateBarristerOwn = Boolean(myBarristerId) && canMutateFinancials(currentUser?.memberType);

  const setActiveMut = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      setBankAccountActive(id, isActive),
    onSuccess: (_data, vars) => {
      toast.success(vars.isActive ? 'Account reactivated' : 'Account deactivated');
      qc.invalidateQueries({ queryKey: ['bank-accounts'] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not update account')),
  });

  const grouped = useMemo(() => {
    const out: Record<BankAccountType, BankAccount[]> = {
      operating: [],
      trust: [],
      other: [],
    };
    if (!accountsQ.data) return out;
    for (const a of accountsQ.data) out[a.type].push(a);
    return out;
  }, [accountsQ.data]);

  const totalCount = accountsQ.data?.length ?? 0;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <BackButton onClick={() => navigate(-1)} />
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Bank accounts
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Configure operating and trust accounts. The last-4 digits and sort code show on
            receipts and invoices so clients know where to send payment.
          </p>
        </div>
        {(isAdmin || canCreateBarristerOwn) && (
          <Button onClick={() => setCreateOpen(true)} className="gap-1.5">
            <Plus size={14} weight="bold" /> Add account
          </Button>
        )}
      </div>

      {/* Scope tabs — only visible to barristers; clerks see chambers-only */}
      {myBarristerId && (
        <div className="no-scrollbar -mb-px flex items-center gap-1 overflow-x-auto overflow-y-hidden border-b border-border">
          {(
            [
              { key: 'mine' as const, label: 'My accounts' },
              { key: 'chambers' as const, label: 'Chambers shared' },
              ...(isAdmin
                ? [{ key: 'all' as const, label: 'Everything in chambers' }]
                : []),
            ]
          ).map((t) => (
            <button
              key={t.key}
              onClick={() => setScope(t.key)}
              className={`shrink-0 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition ${
                scope === t.key
                  ? 'border-brand-600 text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card className="px-4 py-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-0.5 block text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
              Type
            </label>
            <select
              value={typeFilter ?? ''}
              onChange={(e) =>
                setTypeFilter(e.target.value ? (e.target.value as BankAccountType) : undefined)
              }
              className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            >
              <option value="">All</option>
              <option value="operating">Operating</option>
              <option value="trust">Trust</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="mb-0.5 block text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
              Status
            </label>
            <select
              value={activeFilter ?? ''}
              onChange={(e) =>
                setActiveFilter(
                  e.target.value === '' ? undefined : (e.target.value as 'true' | 'false'),
                )
              }
              className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            >
              <option value="true">Active only</option>
              <option value="false">Inactive only</option>
              <option value="">All</option>
            </select>
          </div>
        </div>
      </Card>

      {accountsQ.isLoading && (
        <Card className="flex items-center gap-2 px-5 py-6 text-sm text-muted-foreground">
          <CircleNotch size={14} className="animate-spin" /> Loading accounts…
        </Card>
      )}

      {accountsQ.isError && (
        <Card className="px-5 py-6 text-sm text-red-700 dark:text-red-300">
          {getApiErrorMessage(accountsQ.error, 'Could not load bank accounts.')}
        </Card>
      )}

      {accountsQ.data && totalCount === 0 && (
        <Card className="px-5 py-12 text-center">
          <Bank className="mx-auto mb-3 h-9 w-9 text-muted-foreground" weight="duotone" />
          <div className="mb-1 text-sm font-medium">No accounts yet</div>
          <p className="mx-auto max-w-sm text-xs text-muted-foreground">
            {isAdmin
              ? 'Add your chambers\' operating account so receipts and invoices can show payment details. Add a trust account too if you hold client money.'
              : 'No bank accounts have been configured yet. Ask the chambers admin to set them up.'}
          </p>
          {isAdmin && (
            <Button onClick={() => setCreateOpen(true)} className="mt-4 gap-1.5">
              <Plus size={14} weight="bold" /> Add first account
            </Button>
          )}
        </Card>
      )}

      {!isAdmin && !canCreateBarristerOwn && accountsQ.data && totalCount > 0 && (
        <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          Read-only view — only chambers admins and the owning barrister can edit bank accounts.
        </div>
      )}
      {scope === 'chambers' && canCreateBarristerOwn && accountsQ.data && totalCount > 0 && (
        <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          Chambers-shared accounts — only chambers admins can edit. Switch to <strong>My accounts</strong> to manage your own.
        </div>
      )}

      {/* Grouped sections */}
      {accountsQ.data && totalCount > 0 && (
        <div className="space-y-4">
          {(['operating', 'trust', 'other'] as BankAccountType[]).map((t) => {
            const accounts = grouped[t];
            if (accounts.length === 0) return null;
            const meta = typeBadge(t);
            return (
              <Card key={t} className="overflow-hidden">
                <div className="flex items-center gap-2 border-b border-border bg-muted/30 px-5 py-2.5 text-xs">
                  <span
                    className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider ${meta.classes}`}
                  >
                    {meta.icon} {meta.label}
                  </span>
                  <span className="text-muted-foreground">
                    {accounts.length} {accounts.length === 1 ? 'account' : 'accounts'}
                  </span>
                </div>
                {/* A table cannot reflow. Seven columns at desktop padding ran off a phone with
                    no way to reach what was past the edge — which was Status and the row's own
                    Edit / Deactivate buttons, so an account could not be managed on a phone at
                    all. Below `md` the payment details fold into a line under the account name
                    and only Name, Status and Actions stay as columns; `overflow-x-auto` is the
                    backstop for anything still too wide. Unchanged from `md` up. */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b border-border text-xs uppercase tracking-[0.06em] text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium sm:px-5">Name</th>
                        <th className="hidden px-4 py-2 text-left font-medium md:table-cell">Bank</th>
                        <th className="hidden px-4 py-2 text-left font-medium md:table-cell">Sort code</th>
                        <th className="hidden px-4 py-2 text-left font-medium md:table-cell">Account</th>
                        <th className="hidden px-4 py-2 text-left font-medium md:table-cell">Currency</th>
                        <th className="px-3 py-2 text-left font-medium sm:px-4">Status</th>
                        <th className="px-3 py-2 text-right font-medium sm:px-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {accounts.map((a) => (
                        <tr key={a.id} className={`hover:bg-muted/20 ${!a.isActive ? 'opacity-60' : ''}`}>
                          <td className="px-3 py-2.5 sm:px-5">
                            <div className="flex items-center gap-1.5 font-medium">
                              {a.name}
                              {a.isDefault && (
                                <span title="Default account for this type">
                                  <Star size={11} weight="fill" className="text-amber-500" />
                                </span>
                              )}
                            </div>
                            {a.description && (
                              <div className="truncate text-xs text-muted-foreground">
                                {a.description}
                              </div>
                            )}
                            {/* The four folded columns, on one line, below md only. Nothing a
                                phone can no longer see — it just reads downwards. */}
                            <div className="mt-0.5 text-[11.5px] text-muted-foreground md:hidden">
                              {[a.bankName, formatSortCode(a.sortCode), formatAccountMasked(a.accountNumberLast4), a.currency]
                                .filter((v): v is string => !!v && v !== '—')
                                .join(' · ')}
                            </div>
                          </td>
                          <td className="hidden px-4 py-2.5 text-[12.5px] md:table-cell">
                            {a.bankName ?? <span className="text-muted-foreground">—</span>}
                          </td>
                          <td className="hidden px-4 py-2.5 text-[12.5px] md:table-cell">
                            {formatSortCode(a.sortCode)}
                          </td>
                          <td className="hidden px-4 py-2.5 text-[12.5px] md:table-cell">
                            {formatAccountMasked(a.accountNumberLast4)}
                          </td>
                          <td className="hidden px-4 py-2.5 text-[12.5px] md:table-cell">{a.currency}</td>
                          <td className="px-3 py-2.5 sm:px-4">
                            <Badge variant={a.isActive ? 'default' : 'outline'}>
                              {a.isActive ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="whitespace-nowrap px-3 py-2.5 text-right sm:px-4">
                            {canEditAccount(a) && (
                              <div className="inline-flex items-center gap-1">
                                <button
                                  onClick={() => setEditTarget(a)}
                                  title="Edit"
                                  className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground"
                                >
                                  <PencilSimple size={13} />
                                </button>
                                {a.isActive ? (
                                  <button
                                    onClick={() => {
                                      if (window.confirm(`Deactivate "${a.name}"? It will be hidden from receipts but kept for audit.`)) {
                                        setActiveMut.mutate({ id: a.id, isActive: false });
                                      }
                                    }}
                                    title="Deactivate"
                                    className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition hover:bg-amber-50 dark:bg-amber-500/10 hover:text-amber-700 dark:hover:text-amber-300 dark:text-amber-300"
                                  >
                                    <Pause size={13} />
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => setActiveMut.mutate({ id: a.id, isActive: true })}
                                    title="Reactivate"
                                    className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition hover:bg-emerald-50 dark:bg-emerald-500/10 hover:text-emerald-700 dark:hover:text-emerald-300 dark:text-emerald-300"
                                  >
                                    <Play size={13} />
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {createOpen && (
        <BankAccountModal
          onClose={() => setCreateOpen(false)}
          onSaved={() => setCreateOpen(false)}
        />
      )}
      {editTarget && (
        <BankAccountModal
          initial={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => setEditTarget(null)}
        />
      )}
    </div>
  );
}
